import React, {
  useState, useCallback, useEffect, useRef,
} from 'react';
import Shimmer from 'react-shimmer-effect';
import { useDispatch, useSelector } from 'react-redux';
import IcomoonReact from 'icomoon-react';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import ResizeObserver from 'react-resize-observer';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import SwapPropDropdown from './SwapPropDropdown';
import AddPropertyColumnComponent from './AddPropertyColumnComponent';
import Text from '../../../../shared/components/Text/Text';
import {
  saveCalendarExercise,
  updateReduxCalendarItem,
  updateReduxProgramItem,
  focusNextPreSaveItem,
  focusNextPreSaveProgramItem,
  setPreSaveTableLocalState,
  saveNonDateBasedWorkoutExercise,
} from '../../ducks/calendarActions';
import capitalizeWords from '../../../../shared/utils/capitalizeWords';
import generateUniqueId from '../../../../shared/utils/generateUniqueId';
import { showErrorToast } from '../../../../shared/utils/toastManager';

const FormWrapper = styled('form')`
  display: flex;
  width: 100%;
`;

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow-x: scroll;
`;

const PrimaryRow = styled('div')`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
  margin-top: ${(props) => (props.isAdding ? '16px' : '0px')};
`;

const ValueRow = styled('div')`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 16px;
`;

const PrimaryRowValue = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  max-height: ${(props) => (props.isAdding && '24px')};
  margin-bottom: 3px;
`;

const RowValue = styled('input')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  background: ${(props) => props.background};
  border: ${(props) => (!props.isError ? '1px solid #EEEEEE' : '1px solid red')};
  text-align: center;
  border-radius: 4px;
  color: ${(props) => (props.textColor ? props.textColor : '#444444')};

  ::placeholder {
    color: ${(props) => (props.textColor ? props.textColor : '#444444')};
  }
`;

const SetRowValue = styled('input')`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  background: #FCFCFC;
  border: 1px solid #EEEEEE;
  text-align: center;
  border-radius: 4px;
`;

const SaveButtonContainer = styled('button')`
  display: flex;
  background: #10cd8c;
  height: 34px;
  padding-left: 8px;
  padding-right: 8px;
  font-family: 'Nunito Sans';
  color: ${(props) => (props.shimmer ? 'transparent' : 'white')};
  border-radius: 5px;
  font-weight: 700;
  font-size: 13px;
  align-items: center;
  min-width: 56px;
  border-radius: 4px;
  cursor: pointer;
  border: none;
  width: 100%;
`;

const RemoveIcon = styled('div')`
  padding: 4px 4px 4px 8px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const RowsContainer = styled('div')`
  height: 40px;
  display: flex;
  width: 100%;
  align-items: flex-end;
  gap: 16px;
`;

const ErrorAndRowsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const RowValueContainer = styled('div')`
  flex: 1;
  min-width: 160px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  border-radius: ${(props) => (props.shimmer ? '4px' : '0px')};
`;

const SetRowValueContainer = styled('div')`
  flex: 0 0 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: ${(props) => (props.shimmer ? '4px' : '0px')};
`;

const NameAndDropdown = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 3px;
  position: relative;
`;

const InfoIconWrapper = styled('div')`
  position: absolute;
  left: -20px;
  display: flex;
  align-items: center;
`;

const PrimaryValueContainer = styled('div')`
  flex: 1;
  min-width: 160px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${(props) => (props.shimmer ? 'transparent' : 'inherit')};
  border-radius: ${(props) => (props.shimmer ? '4px' : '0px')};
  padding-top: ${(props) => (props.shimmer ? '0px' : '16px')};
`;

const SetPrimaryValueContainer = styled('div')`
  flex: 0 0 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${(props) => (props.shimmer ? 'transparent' : 'inherit')};
  border-radius: ${(props) => (props.shimmer ? '4px' : '0px')};
  padding-top: ${(props) => (props.shimmer ? '0px' : '16px')};
`;

const ErrorText = styled(Text)`
  color: red;
  font-size: 12px;
  margin-top: 4px;
  margin-left: ${(props) => props.containerWidthForSetRowValueContainer && props.containerWidthForSetRowValueContainer + 16}px;
`;

const ShimmerRowValue = styled('div')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  text-align: center;
  border-radius: 6px;
  color: transparent;
  margin-left: 5px;
  margin-right: 5px;
`;

const ShimmerSetRowValue = styled('div')`
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  font-family: 'Nunito Sans';
  width: 100%;
  height: 32px;
  text-align: center;
  margin-right: 5px;
`;

const SaveButtonWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-left: 16px;
  padding-top: 18px;
  margin-top: 32px;
`;

const SaveButtonContainerText = styled('div')`
  margin-top: 2px;
  font-weight: 700;
  font-family: 'Nunito Sans';
`;

const PreSaveTable = ({
  completionOnly,
  coachCompletion,
  isLeftAndRight,
  contentIndex,
  content,
  trackRepCount,
  trackVolumeLoad,
  localAdditionalInfo,
  swapDropdownSelectedExercise,
  forcePrUpdate,
  disablePrUpdate,
  measure,
  date,
  dayNumber,
  setCoachCompletion,
  setCompletionOnly,
  setIsLeftAndRight,
  setTrackVolumeLoad,
  setTrackRepCount,
  setForcePrUpdate,
  setDisablePrUpdate,
}) => {
  const dispatch = useDispatch();
  const tableContainerRef = useRef(null);
  const [
    containerWidthForSetRowValueContainer, setContainerWidthForSetRowValueContainer,
  ] = useState(0);
  const [setNumberValue, setSetNumberValue] = useState(1);
  const [percentPR, setPercentPR] = useState('');
  const [isBodyweight, setIsBodyweight] = useState(!!content?.defaultOptions?.liftRepsOnly);
  const [isTime, setIsTime] = useState(false);
  const [error, setError] = useState(null); // Track error message
  const [tempo, setTempo] = useState(null);
  const [rest, setRest] = useState(null);
  const [load, setLoad] = useState(null);
  const [loadInputValue, setLoadInputValue] = useState(''); // Track the raw load input string
  const [isAdding, setIsAdding] = useState(false);
  const [peakPowerEnum, setPeakPowerEnum] = useState(1);
  const [repsInputValue, setRepsInputValue] = useState(''); // Track the raw reps input string

  // Generate unique IDs for tooltips
  const repsTooltipId = `reps-tooltip-${content.assignedId}`;
  const peakPowerTooltipId = `peak-power-tooltip-${content.assignedId}`;
  const velocityTooltipId = `velocity-tooltip-${content.assignedId}`;

  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const effectiveDate = date || selectedDate;
  const endpointFormattedDate = moment(date).format('YYYY-MM-DD');
  const focusTarget = useSelector((state) => state.calendar.data.focusTarget);
  const preSaveTableLocalStateForItem = useSelector(
    (state) => state.calendar.data.preSaveTableLocalState[content.assignedId],
  );

  const [exerciseData, setExerciseData] = useState({
    reps: [],
    load: null,
    rest: null,
    tempo: null,
    velocity: null,
    peakPower: null,
  });

  const [primaryRows, setPrimaryRows] = useState([
    { name: 'set' },
    { name: isTime ? 'time' : 'reps' },
    { name: isBodyweight ? 'bodyweight' : 'load' },
    { name: 'rest', label: 'Rest (s)' },
    { name: 'tempo' },
    { name: 'velocity' },
    { name: 'peak power', label: 'Peak Power (w)' },
    { name: '%pr' },
    { name: 'add-property', label: 'Add Property' },
  ]);

  const [primaryRowSwitch, setPrimaryRowSwitch] = useState({
    set: true,
    reps: true,
    load: false,
    bodyweight: !!content.defaultOptions?.liftRepsOnly,
    rest: !!content?.defaultOptions?.liftRestTime,
    tempo: false,
    velocity: !!content?.defaultOptions?.barSpeed,
    'peak power': !!content?.defaultOptions?.peakPower,
    '%pr': false,
    'add-property': true,
  });

  const setPrimaryRowSwitchState = (newState) => {
    setPrimaryRowSwitch(newState);
  };

  const setTempoFunction = (value) => {
    setTempo(value);
  };

  const setRestFunction = (value) => {
    const intRest = parseInt(value, 10);
    // Prevent negative values
    if (intRest < 0) return;
    setRest(intRest);
  };

  const setPercentPRFunction = (value) => {
    setPercentPR(value);
  };

  /** This useEffect detects if the user chooses a different exercise
   * from the item dropdownn select and updates the pre saved item in redux
   * so that the table structure and content updates to match the selected exercise
   */
  useEffect(() => {
    if (swapDropdownSelectedExercise) {
      const updatedItem = {
        ...swapDropdownSelectedExercise,
        title: swapDropdownSelectedExercise.name,
        exerciseId: parseInt(swapDropdownSelectedExercise.id, 10),
        id: `${swapDropdownSelectedExercise.id}-${generateUniqueId()}`,
        assignedId: `${swapDropdownSelectedExercise.id}-${generateUniqueId()}`,
        preSave: true,
        type: swapDropdownSelectedExercise?.type || 'T',
      };
      if (programType === 'free-form-program') {
        dispatch(
          updateReduxProgramItem(updatedItem, dayNumber || selectedProgramDayNumber, contentIndex),
        );
      } else {
        dispatch(updateReduxCalendarItem(updatedItem, endpointFormattedDate, contentIndex));
      }
    }
  }, [swapDropdownSelectedExercise]);

  const addPropertyInitialized = useRef(false);

  // Turn off 'add-property' when all optional properties are enabled
  useEffect(() => {
    // Skip on initial mount to allow backup restoration to complete
    if (!addPropertyInitialized.current) {
      addPropertyInitialized.current = true;
      return;
    }

    const allOptionalPropertiesEnabled = primaryRowSwitch.rest
      && primaryRowSwitch.tempo
      && primaryRowSwitch.velocity
      && primaryRowSwitch['peak power']
      && primaryRowSwitch['%pr']
      && primaryRowSwitch.load;

    if (allOptionalPropertiesEnabled && primaryRowSwitch['add-property']) {
      setPrimaryRowSwitch((prev) => ({
        ...prev,
        'add-property': false,
      }));
    } else if (!allOptionalPropertiesEnabled && !primaryRowSwitch['add-property']) {
      // Re-enable add-property if any optional property is turned off
      setPrimaryRowSwitch((prev) => ({
        ...prev,
        'add-property': true,
      }));
    }
  }, [
    primaryRowSwitch.rest,
    primaryRowSwitch.tempo,
    primaryRowSwitch.velocity,
    primaryRowSwitch['peak power'],
    primaryRowSwitch['%pr'],
    primaryRowSwitch.load,
  ]);

  const hasMounted = useRef(false);

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      /** skip first run because cell measurer wrapper
      * will get that and we don't want to run it twice */
      return;
    }
    if (measure) {
      measure();
    }
  }, [error]);

  useEffect(() => {
    if (isBodyweight) {
      setLoad(null);
      setLoadInputValue('');
    }
    if (isTime) {
      // Update exerciseData to set reps to an empty array while preserving other properties
      setExerciseData((prevExerciseData) => ({
        ...prevExerciseData,
        reps: [], // Set reps to an empty array
      }));
    }
  }, [isBodyweight, isTime]);

  // This useEffect is responsible for managing focus on input fields when the focusTarget changes.
  // It checks if the current focusTarget's assignedId matches the content's assignedId.
  // If they match, it finds the first input element
  // with a data-focus-target attribute equal to the content's assignedId.
  // Once found, it sets a timeout to focus on this
  // input element after a short delay, ensuring the DOM is ready.
  useEffect(() => {
    if (focusTarget?.assignedId === content.assignedId) {
      const firstInput = document.querySelector(`[data-focus-target="${content.assignedId}"]`);
      if (firstInput) {
        setTimeout(() => {
          firstInput.focus();
        }, 100); // Small delay to ensure DOM is ready
      }
    }
  }, [focusTarget, content.assignedId]);

  // Initialize state from Redux backup if it exists
  const backupState = preSaveTableLocalStateForItem;
  const hasRestoredBackupRef = useRef(null);

  useEffect(() => {
    // Only restore once per assignedId — ref guard prevents re-execution
    // when auto-save writes back to the same backup slice
    if (hasRestoredBackupRef.current === content.assignedId) return;
    if (!backupState) return;

    hasRestoredBackupRef.current = content.assignedId;

    if (backupState.setNumberValue !== undefined) setSetNumberValue(backupState.setNumberValue);
    if (backupState.percentPR !== undefined) setPercentPR(backupState.percentPR);
    if (backupState.isBodyweight !== undefined) setIsBodyweight(backupState.isBodyweight);
    if (backupState.isTime !== undefined) setIsTime(backupState.isTime);
    if (backupState.tempo !== undefined) setTempo(backupState.tempo);
    if (backupState.rest !== undefined) setRest(backupState.rest);
    if (backupState.load !== undefined) setLoad(backupState.load);
    if (backupState.loadInputValue !== undefined) setLoadInputValue(backupState.loadInputValue);
    if (backupState.peakPowerEnum !== undefined) setPeakPowerEnum(backupState.peakPowerEnum);
    if (backupState.repsInputValue !== undefined) setRepsInputValue(backupState.repsInputValue);
    if (backupState.exerciseData !== undefined) setExerciseData(backupState.exerciseData);
    if (backupState.primaryRowSwitch !== undefined) {
      setPrimaryRowSwitch(backupState.primaryRowSwitch);
    }
    if (backupState.primaryRows !== undefined) setPrimaryRows(backupState.primaryRows);
    if (backupState.coachCompletion !== undefined) setCoachCompletion(backupState.coachCompletion);
    if (backupState.completionOnly !== undefined) setCompletionOnly(backupState.completionOnly);
    if (backupState.isLeftAndRight !== undefined) setIsLeftAndRight(backupState.isLeftAndRight);
    if (backupState.trackVolumeLoad !== undefined) setTrackVolumeLoad(backupState.trackVolumeLoad);
    if (backupState.trackRepCount !== undefined) setTrackRepCount(backupState.trackRepCount);
    if (backupState.forcePrUpdate !== undefined) setForcePrUpdate(backupState.forcePrUpdate);
    if (backupState.disablePrUpdate !== undefined) setDisablePrUpdate(backupState.disablePrUpdate);
  }, [
    content.assignedId,
    backupState,
    setCoachCompletion,
    setCompletionOnly,
    setIsLeftAndRight,
    setTrackVolumeLoad,
    setTrackRepCount,
    setForcePrUpdate,
    setDisablePrUpdate,
  ]);

  // Save current state to Redux backup on blur
  const saveStateToRedux = useCallback(() => {
    const stateObject = {
      setNumberValue,
      percentPR,
      isBodyweight,
      isTime,
      tempo,
      rest,
      load,
      loadInputValue,
      peakPowerEnum,
      repsInputValue,
      exerciseData,
      primaryRowSwitch,
      primaryRows,
      coachCompletion,
      completionOnly,
      isLeftAndRight,
      trackVolumeLoad,
      trackRepCount,
      forcePrUpdate,
      disablePrUpdate,
    };
    dispatch(setPreSaveTableLocalState(content.assignedId, stateObject));
  }, [
    setNumberValue, percentPR, isBodyweight, isTime, tempo, rest, load,
    loadInputValue, peakPowerEnum, repsInputValue, exerciseData, primaryRowSwitch, primaryRows,
    coachCompletion, completionOnly, isLeftAndRight, trackVolumeLoad,
    trackRepCount, forcePrUpdate, disablePrUpdate,
    content.assignedId, dispatch,
  ]);

  const autoSaveInitialized = useRef(false);

  // Auto-save state to Redux on every change (not just blur)
  // This ensures state is preserved when component re-renders (e.g., superset selection)
  useEffect(() => {
    // Skip the initial render cycle - only save after user makes changes
    if (!autoSaveInitialized.current) {
      autoSaveInitialized.current = true;
      return;
    }
    saveStateToRedux();
  }, [saveStateToRedux]);

  const handleInputChange = (field, value) => {
    if (field === 'reps' || field === 'time') {
      // Update the raw input value for reps field
      if (field === 'reps') {
        setRepsInputValue(value);

        // Check for partial "custom" input
        const trimmedValue = value.trim().toLowerCase();
        if (['c', 'cu', 'cus', 'cust'].includes(trimmedValue)) {
          setError('If you would like to program multiple sets, please enter them as comma separated values, e.g. 12,10,8 to match the number of sets');
          return;
        }

        // For reps, store the raw array and validate without auto-normalization
        const repsArray = value
          .split(',')
          .map((rep) => rep.trim())
          .filter(Boolean);

        setExerciseData((prev) => ({
          ...prev,
          reps: repsArray,
        }));

        // Validate format but don't auto-normalize
        const setCount = parseInt(setNumberValue, 10);
        const invalidValues = [];

        // Check each value for valid format
        repsArray.forEach((rep, index) => {
          let isValid = false;

          if (/^\d+\+?$/.test(rep)) {
            // Single number, optionally followed by a plus sign
            isValid = true;
          } else if (/^\d+-\d+$/.test(rep)) {
            // Range - validate that first number is less than second number
            const [start, end] = rep.split('-').map(Number);
            isValid = start <= end;
          } else if (['AMAP'].includes(rep.toUpperCase())) {
            // AMRAP/AMAP
            isValid = true;
          }

          if (!isValid) {
            invalidValues.push({ value: rep, position: index + 1 });
          }
        });

        // Show format errors
        if (invalidValues.length > 0) {
          if (invalidValues.length === 1) {
            setError(`Invalid format: "${invalidValues[0].value}" at position ${invalidValues[0].position}. Use numbers, ranges with lower value first (e.g. 8-12), minimums (e.g. 8+), or "AMAP".`);
          } else {
            const invalidList = invalidValues.map((iv) => `"${iv.value}" (pos ${iv.position})`).join(', ');
            setError(`Invalid format for values: ${invalidList}. Use numbers, ranges with lower value first (e.g. 8-12), minimums (e.g. 8+), or "AMAP".`);
          }
          return;
        }

        // Show count mismatch errors (but don't auto-normalize)
        if (repsArray.length !== setCount && repsArray.length !== 1 && repsArray.length > 0) {
          setError('Enter a number for reps to apply to all sets.');
        } else {
          setError(null); // Clear error if valid
        }

        return;
      }

      // Handle time field (existing logic)
      const repsArray = value
        .split(',')
        .map((rep) => rep.trim())
        .filter(Boolean);
      const setCount = parseInt(setNumberValue, 10); // Number of sets

      let isValidRepsInput;
      const invalidValues = [];

      /** if field is time we are only allowing a number input so set to true */
      if (field === 'time') {
        isValidRepsInput = true;
      }

      // Temporary store value for validation
      if (!isValidRepsInput) {
        if (invalidValues.length === 1) {
          setError(`Invalid format: "${invalidValues[0].value}" at position ${invalidValues[0].position}. Use numbers, ranges (e.g. 8-12), minimums (e.g. 8+), or "AMAP".`);
        } else {
          const invalidList = invalidValues.map((iv) => `"${iv.value}" (pos ${iv.position})`).join(', ');
          setError(`Invalid format for values: ${invalidList}. Use numbers, ranges (e.g. 8-12), minimums (e.g. 8+), or "AMAP".`);
        }
        return;
      }

      // Only normalize when fully valid
      if (repsArray.length === setCount || repsArray.length === 1) {
        const normalizedReps = repsArray.length === 1
          ? Array(setCount).fill(repsArray[0]) : repsArray;
        setExerciseData((prev) => ({
          ...prev,
          reps: normalizedReps,
        }));
        setError(null); // Clear error
      } else {
        // Allow partial input, but don't overwrite reps
        setExerciseData((prev) => ({
          ...prev,
          reps: repsArray,
        }));
        setError(
          `Expected ${setCount} values or 1 value (to apply to all sets), but received ${repsArray.length} values.`,
        );
      }
    } else if (field === 'sets') {
      // Handle sets field change and re-validate reps
      setSetNumberValue(value);

      // Re-validate existing reps against new set count
      if (exerciseData.reps && exerciseData.reps.length > 0) {
        const newSetCount = parseInt(value, 10);
        const repsArray = exerciseData.reps;

        // Check if reps count matches new set count
        if (repsArray.length !== newSetCount && repsArray.length !== 1 && repsArray.length > 0) {
          setError('Enter a number for reps to apply to all sets.');
        } else {
          setError(null); // Clear error if valid
        }
      }

      // Re-validate existing load values against new set count
      if (loadInputValue && loadInputValue.trim() !== '') {
        const newSetCount = parseInt(value, 10);
        const loadArray = loadInputValue
          .split(',')
          .map((loadVal) => loadVal.trim())
          .filter(Boolean);

        // Check if load count matches new set count
        if (loadArray.length !== newSetCount && loadArray.length !== 1 && loadArray.length > 0) {
          setError('Enter a number for load to apply to all sets.');
        }
      }
    } else {
      // Update other fields normally
      setExerciseData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleRepsBlur = () => {
    if (Array.isArray(exerciseData.reps)) {
      const setCount = parseInt(setNumberValue, 10);

      // Check if all reps are valid and collect invalid ones
      const invalidValues = [];
      exerciseData.reps.forEach((rep, index) => {
        const isValid = /^\d+\+?$/.test(rep) // Single number, optionally followed by a plus sign
          || /^\d+-\d+$/.test(rep) // Range
          || ['AMAP'].includes(rep.toUpperCase()); // AMRAP/AMAP

        if (!isValid) {
          invalidValues.push({ value: rep, position: index + 1 });
        }
      });

      // If any rep is invalid, show specific error
      if (invalidValues.length > 0) {
        if (invalidValues.length === 1) {
          setError(`Invalid format: "${invalidValues[0].value}" at set ${invalidValues[0].position}. Use numbers, ranges (e.g. 8-12), minimums (e.g. 8+), or "AMAP".`);
        } else {
          const invalidList = invalidValues.map((iv) => `"${iv.value}" (set ${iv.position})`).join(', ');
          setError(`Invalid format for values: ${invalidList}. Use numbers, ranges (e.g. 8-12), minimums (e.g. 8+), or "AMAP".`);
        }
        return;
      }

      // Normalize single values to fill all sets on blur
      if (exerciseData.reps.length === 1) {
        const normalizedReps = Array(setCount).fill(exerciseData.reps[0]);
        setExerciseData((prev) => ({
          ...prev,
          reps: normalizedReps,
        }));
        // Update the input display to show the normalized version
        setRepsInputValue(normalizedReps.join(', '));
        setError(null);
      } else if (exerciseData.reps.length === setCount) {
        setError(null); // Clear error if valid
      } else {
        setError(`Expected ${setCount} values or 1 value (to apply to all sets), but have ${exerciseData.reps.length} values.`);
      }
    } else {
      setError(`Invalid input type. Expected comma-separated values but received: ${typeof exerciseData.reps}.`);
    }
  };

  const handleRepsKeyDown = (e) => {
    const { key, target } = e;
    const { value, selectionStart, selectionEnd } = target;

    // Handle 'a' or 'A' key press for AMAP auto-expansion
    if (key.toLowerCase() === 'a') {
      // Check if we're at the start of a new value (after comma + space, or at the beginning)
      const beforeCursor = value.substring(0, selectionStart);
      const isAtValueStart = beforeCursor === '' || beforeCursor.endsWith(', ') || beforeCursor.endsWith(',');

      if (isAtValueStart) {
        e.preventDefault();
        const beforeSelection = value.substring(0, selectionStart);
        const afterSelection = value.substring(selectionEnd);
        const newValue = `${beforeSelection}AMAP${afterSelection}`;
        const newCursorPosition = selectionStart + 4; // Position after 'AMAP'

        setRepsInputValue(newValue);
        handleInputChange('reps', newValue);

        // Set cursor position directly using requestAnimationFrame for better timing
        requestAnimationFrame(() => {
          target.setSelectionRange(newCursorPosition, newCursorPosition);
        });
      }
    }

    // Handle backspace on AMAP
    if (key === 'Backspace') {
      // Check if cursor is at the end of 'AMAP'
      const beforeCursor = value.substring(0, selectionStart);
      const afterCursor = value.substring(selectionEnd);

      // Find if there's an AMAP ending right before the cursor
      if (beforeCursor.endsWith('AMAP') && selectionStart === selectionEnd) {
        e.preventDefault();

        // Remove the entire 'AMAP' word
        const newBeforeCursor = beforeCursor.substring(0, beforeCursor.length - 4);
        const newValue = newBeforeCursor + afterCursor;
        const newCursorPosition = newBeforeCursor.length;

        setRepsInputValue(newValue);
        handleInputChange('reps', newValue);

        // Set cursor position directly using requestAnimationFrame
        requestAnimationFrame(() => {
          target.setSelectionRange(newCursorPosition, newCursorPosition);
        });
      }
    }
  };

  const handlePercentPRBlur = () => {
    if (percentPR) {
      const prArray = percentPR
        .split(',')
        .map((pr) => pr.trim())
        .filter(Boolean);
      const setCount = parseInt(setNumberValue, 10);

      // Check if all %PR values are valid and collect invalid ones
      const invalidValues = [];
      prArray.forEach((pr, index) => {
        const isValid = /^\d+$/.test(pr); // Only whole numbers allowed
        if (!isValid) {
          invalidValues.push({ value: pr, position: index + 1 });
        }
      });

      // If any %PR is invalid, show specific error
      if (invalidValues.length > 0) {
        if (invalidValues.length === 1) {
          setError(`Invalid %PR format: "${invalidValues[0].value}" at set ${invalidValues[0].position}. Use whole numbers only.`);
        } else {
          const invalidList = invalidValues.map((iv) => `"${iv.value}" (set ${iv.position})`).join(', ');
          setError(`Invalid %PR format for values: ${invalidList}. Use whole numbers only.`);
        }
        return;
      }

      // Check if the number of %PR values matches the set count
      if (prArray.length !== setCount && prArray.length !== 1) {
        setError(`Expected ${setCount} %PR values or 1 value (to apply to all sets), but have ${prArray.length} values.`);
      } else {
        setError(null); // Clear error if valid
      }
    } else {
      // Clear error if field is empty
      setError(null);
    }
  };

  const handleLoadInputChange = (value) => {
    setLoadInputValue(value);

    // For load, store the raw array and validate without auto-normalization
    const loadArray = value
      .split(',')
      .map((loadVal) => loadVal.trim())
      .filter(Boolean);

    // Validate format but don't auto-normalize
    const setCount = parseInt(setNumberValue, 10);
    const invalidValues = [];

    // Check each value for valid format (positive numbers, decimals allowed)
    loadArray.forEach((loadVal, index) => {
      const isValid = /^\d+(\.\d+)?$/.test(loadVal); // Positive numbers, decimals allowed
      if (!isValid) {
        invalidValues.push({ value: loadVal, position: index + 1 });
      } else if (parseFloat(loadVal) < 0) {
        invalidValues.push({ value: loadVal, position: index + 1 });
      }
    });

    // Show format errors
    if (invalidValues.length > 0) {
      if (invalidValues.length === 1) {
        setError(`Invalid load format: "${invalidValues[0].value}" at position ${invalidValues[0].position}. Use positive numbers only.`);
      } else {
        const invalidList = invalidValues.map((iv) => `"${iv.value}" (pos ${iv.position})`).join(', ');
        setError(`Invalid load format for values: ${invalidList}. Use positive numbers only.`);
      }
      return;
    }

    // Show count mismatch errors (but don't auto-normalize)
    if (loadArray.length !== setCount && loadArray.length !== 1 && loadArray.length > 0) {
      setError('Enter a number for load to apply to all sets.');
    } else {
      setError(null); // Clear error if valid
    }
  };

  const handleLoadBlur = () => {
    if (loadInputValue && loadInputValue.trim() !== '') {
      const loadArray = loadInputValue
        .split(',')
        .map((loadVal) => loadVal.trim())
        .filter(Boolean);
      const setCount = parseInt(setNumberValue, 10);

      // Check if all load values are valid and collect invalid ones
      const invalidValues = [];
      loadArray.forEach((loadVal, index) => {
        const isValid = /^\d+(\.\d+)?$/.test(loadVal) && parseFloat(loadVal) >= 0;
        if (!isValid) {
          invalidValues.push({ value: loadVal, position: index + 1 });
        }
      });

      // If any load is invalid, show specific error
      if (invalidValues.length > 0) {
        if (invalidValues.length === 1) {
          setError(`Invalid load format: "${invalidValues[0].value}" at set ${invalidValues[0].position}. Use positive numbers only.`);
        } else {
          const invalidList = invalidValues.map((iv) => `"${iv.value}" (set ${iv.position})`).join(', ');
          setError(`Invalid load format for values: ${invalidList}. Use positive numbers only.`);
        }
        return;
      }

      // Normalize single values to fill all sets on blur
      if (loadArray.length === 1) {
        const normalizedLoad = Array(setCount).fill(loadArray[0]);
        setLoadInputValue(normalizedLoad.join(', '));
        setError(null);
      } else if (loadArray.length === setCount) {
        setError(null); // Clear error if valid
      } else {
        setError(`Expected ${setCount} load values or 1 value (to apply to all sets), but have ${loadArray.length} values.`);
      }
    } else {
      // Clear error if field is empty
      setError(null);
    }
  };

  const typeFormatter = (typeDescription) => {
    if (typeDescription === 'lift') {
      return 'lift';
    }
    if (typeDescription === 'cardio') {
      return 'saq+c';
    }
    if (typeDescription === 'circuit') {
      return 'circuit';
    }
    return '';
  };

  const errorAddReset = () => {
    setIsAdding(false);
  };

  /**
 * Handles the saving of a calendar exercise by preparing and dispatching the required data.
 *
 * @param {number} contentIndexParam - Index of the content item being saved.
 */
  const handleSaveCalendarExercise = (contentIndexParam) => {
    // Check if reps field is empty when reps are enabled
    if (primaryRowSwitch.reps && (!exerciseData.reps || exerciseData.reps.length === 0 || (exerciseData.reps.length === 1 && exerciseData.reps[0] === ''))) {
      showErrorToast('Reps field cannot be empty');
      return;
    }

    // Proceed only if there are no errors.
    if (!error) {
    // Indicate that the saving process has started.
      setIsAdding(true);

      // Parse load values if present
      let loadArray = [];
      if (loadInputValue && loadInputValue.trim() !== '' && !isBodyweight) {
        loadArray = loadInputValue
          .split(',')
          .map((loadVal) => loadVal.trim())
          .filter(Boolean);
      }

      // Generate an array representing the details for each set.
      const setSubmissionArray = Array.from({ length: setNumberValue }, (_, i) => {
        const setData = {
          setNumber: i + 1,
          reps: exerciseData.reps[i]
          ?? exerciseData.reps[0],
          // Use specific reps for the set, defaulting to the first value if undefined.
        };

        // Handle load mapping to individual sets
        if (loadArray.length > 0) {
          setData.prescribedValue = parseFloat(loadArray[i]
            ?? loadArray[0]); // Use specific load for the set, defaulting to first value
        }

        // Handle %PR mapping to individual sets
        if (percentPR) {
          const prArray = percentPR
            .split(',')
            .map((pr) => pr.trim())
            .filter(Boolean);
          if (prArray.length > 0) {
            setData.percent = prArray[i]
            ?? prArray[0]; // Use specific %PR for the set, defaulting to first value
          }
        }

        return setData;
      });

      // Create the main submission object with exercise details.
      const submissionObject = {
        coachCompletion, // Indicates if the coach marks this exercise as complete.
        completionOnly, // If true, only completion is tracked.
        completionRequired: true, // Ensure completion is required for this exercise.
        eachSide: isLeftAndRight, // Flag for tracking each side of the body separately.
        trackRepCount, // Whether to track the number of reps.
        trackVolumeLoad, // Whether to track volume load.
        forceMaxUpdate: forcePrUpdate,
        maxTrackingDisabled: disablePrUpdate,
        repIntensityUnit: null, // Reserved for rep intensity unit, currently null.
        type: content.type === 'T' ? 'tag' : typeFormatter(content.typeDescription), // Exercise type derived from a formatter.
        sets: setSubmissionArray, // Array of set details.
        showSessionBreakItems: false, // Whether session break items are shown (disabled here).
        repsOnly: isBodyweight, // Indicates if this is a bodyweight-only exercise.
        repType: isTime ? 'time' : 'reps', // Specifies whether the exercise tracks time or reps.
        groupingLetter: content.groupingLetter,
        /** Conditional key based on programType: "date" OR "dayNumber" */
        [programType === 'free-form-program' ? 'dayNumber' : 'date']:
        programType === 'free-form-program'
          ? dayNumber
          : moment(effectiveDate).format('YYYY-MM-DD'),
      };

      if (content.type === 'T') {
        submissionObject.tagId = content.exerciseId;
      } else {
        submissionObject.exerciseId = content.exerciseId;
      }

      // Optional properties based on exercise settings.
      if (tempo && primaryRowSwitch.tempo) {
        submissionObject.tempo = tempo; // Add tempo if applicable.
      }
      if (content.description) {
        submissionObject.description = content.description;
        // Include exercise description if provided.
      }
      if (peakPowerEnum && primaryRowSwitch['peak power']) {
        submissionObject.trackPeakPower = peakPowerEnum; // Add peak power tracking if enabled.
      }
      if (rest && primaryRowSwitch.rest) {
        submissionObject.restTime = rest; // Add rest time between sets if specified.
      }
      if (primaryRowSwitch.velocity) {
        submissionObject.trackBarSpeed = primaryRowSwitch.velocity; // Track bar speed if enabled.
      }
      if (localAdditionalInfo) {
        submissionObject.additionalInfo = localAdditionalInfo; // Include any additional local info.
      }

      // Format the date for dispatching.
      const formattedDate = moment(date).format('YYYY-MM-DD');

      // Dispatch the save action with the submission object.
      if (programType === 'free-form-program') {
        dispatch(saveNonDateBasedWorkoutExercise(
          currentUser, // Current user performing the save.
          contentIndexParam, // Index of the content item.
          submissionObject, // Submission details.
          dayNumber, // program 0 indexed dayNumber.
          errorAddReset, // Error handling/reset callback.
          {
            onSuccess: () => {
            // Trigger focus to next preSave item
              dispatch(focusNextPreSaveProgramItem(contentIndexParam, dayNumber));
            },
          },
          content.assignedId,
        ));
      } else {
        dispatch(saveCalendarExercise(
          currentUser, // Current user performing the save.
          contentIndexParam, // Index of the content item.
          submissionObject, // Submission details.
          formattedDate, // Formatted date.
          errorAddReset, // Error handling/reset callback.
          {
            onSuccess: () => {
            // Trigger focus to next preSave item
              dispatch(focusNextPreSaveItem(contentIndexParam, effectiveDate));
            },
          },
          content.assignedId,
        ));
      }
    }
  };

  const rowValueDecider = (name, shouldShimmer) => {
    if (shouldShimmer) {
      return (
        <Shimmer>
          <RowValueContainer shimmer>
            <ShimmerRowValue />
          </RowValueContainer>
        </Shimmer>
      );
    }
    if (name === 'tempo') {
      return (
        <RowValueContainer>
          <RowValue
            value={tempo}
            onChange={(e) => setTempoFunction(e.target.value)}
            onBlur={saveStateToRedux}
            background='#FCFCFC'
          />
        </RowValueContainer>
      );
    }
    if (name === 'rest') {
      return (
        <RowValueContainer>
          <RowValue
            type='number'
            value={rest}
            onChange={(e) => setRestFunction(e.target.value)}
            onBlur={saveStateToRedux}
            background='#FCFCFC'
          />
        </RowValueContainer>
      );
    }
    if (name === '%pr') {
      return (
        <RowValueContainer>
          <RowValue
            type='text'
            value={percentPR}
            onChange={(e) => setPercentPRFunction(e.target.value)}
            background='#FCFCFC'
            onBlur={() => {
              handlePercentPRBlur();
              saveStateToRedux();
            }}
          />
        </RowValueContainer>
      );
    }
    if (name === 'load' || name === 'bodyweight') {
      return (
        <RowValueContainer>
          <RowValue
            isError={name === 'load' && error && !isBodyweight}
            type='text'
            value={isBodyweight ? '' : loadInputValue}
            onChange={(e) => handleLoadInputChange(e.target.value)}
            onBlur={() => {
              handleLoadBlur();
              saveStateToRedux();
            }}
            background='#FCFCFC'
            placeholder={name === 'bodyweight' ? '-' : null}
            textColor={name === 'bodyweight' ? '#BDBDBD' : '#444444'}
            disabled={isBodyweight}
          />
        </RowValueContainer>
      );
    }
    if (name === 'peak power') {
      if (peakPowerEnum === 1) {
        return (
          <RowValueContainer>
            <RowValue
              disabled
              placeholder='-'
              textColor='#BDBDBD'
              background='#FCFCFC'
            />
          </RowValueContainer>
        );
      }
      return (
        <RowValueContainer>
          <RowValue
            disabled
            placeholder='Left'
            textColor='#BDBDBD'
            style={{
              marginRight: 8,
            }}
            background='#FCFCFC'
          />
          <RowValue
            disabled
            placeholder='Right'
            textColor='#BDBDBD'
            style={{
              marginLeft: 8,
            }}
            background='#FCFCFC'
          />
        </RowValueContainer>
      );
    }
    if (name === 'velocity') {
      return (
        <RowValueContainer>
          <RowValue
            type='number'
            disabled
            placeholder='-'
            textColor='#BDBDBD'
          />
        </RowValueContainer>
      );
    }
    return (
      <RowValueContainer>
        <RowValue
          isError={name === 'reps' && error}
          type={name === 'time' ? 'number' : 'test'}
          background='#FCFCFC'
          value={name === 'reps' ? repsInputValue : undefined}
          onChange={(e) => handleInputChange(name, e.target.value)}
          onBlur={name === 'reps' ? () => {
            handleRepsBlur();
            saveStateToRedux();
          } : saveStateToRedux}
          onKeyDown={name === 'reps' ? handleRepsKeyDown : null}
          data-focus-target={
            // Add focus target to the first editable
            // field that's visible (set field takes priority)
            (name === 'reps' && primaryRowSwitch.reps && !primaryRowSwitch.set)
            || (name === 'time' && primaryRowSwitch.time && !primaryRowSwitch.set && !primaryRowSwitch.reps)
            || (name === 'load' && primaryRowSwitch.load && !primaryRowSwitch.set && !primaryRowSwitch.reps && !primaryRowSwitch.time)
              ? content.assignedId
              : undefined
          }
        />
      </RowValueContainer>
    );
  };

  const updateColumnName = useCallback((oldName, newName) => {
    // Prevent updating the add-property column
    if (oldName === 'add-property' || newName === 'add-property') return;

    setPrimaryRows(
      (prevRows2) => prevRows2.map(
        (row) => (row.name === oldName ? { ...row, name: newName } : row),
      ),
    );

    setPrimaryRowSwitch((prevSwitch) => ({
      ...prevSwitch,
      [oldName]: false,
      [newName]: true,
    }));
  }, []);

  const findCorrectUnit = (unitContent) => {
    if (unitContent?.unitAbbr) {
      if (unitContent?.unitAbbr === 'wgt') {
        // orgWeight: 1 = kg, 0 = lbs
        const weightUnit = currentUser?.userSettings?.orgWeight === 1 ? 'kg' : 'lbs';
        return `(${weightUnit})`;
      }
      return `(${unitContent?.unitAbbr})`;
    }
    return '';
  };

  const getTooltipId = (primaryRowName) => {
    if (primaryRowName === 'reps') return repsTooltipId;
    if (primaryRowName === 'peak power') return peakPowerTooltipId;
    if (primaryRowName === 'velocity') return velocityTooltipId;
    return undefined;
  };

  const renderPrimaryRowValue = (primaryRow) => {
    if (!primaryRowSwitch[primaryRow.name]) {
      return null;
    }

    // Handle 'set' column
    if (primaryRow.name === 'set') {
      if (!isAdding) {
        return (
          <SetRowValueContainer>
            <ResizeObserver
              onResize={(rect) => {
                setContainerWidthForSetRowValueContainer(rect?.width);
              }}
            />
            <SetRowValue
              data-focus-target={primaryRowSwitch.set ? content.assignedId : undefined}
              type='number'
              value={setNumberValue}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                // Prevent negative values
                if (value < 0) return;
                handleInputChange('sets', value);
              }}
              onBlur={saveStateToRedux}
            />
          </SetRowValueContainer>
        );
      }
      return (
        <Shimmer>
          <SetRowValueContainer shimmer>
            <ShimmerSetRowValue />
          </SetRowValueContainer>
        </Shimmer>
      );
    }

    // Handle 'add-property' column
    if (primaryRow.name === 'add-property') {
      if (!isAdding) {
        return (
          <RowValueContainer>
            <AddPropertyColumnComponent
              mode='input'
              primaryRows={primaryRows}
              primaryRowSwitch={primaryRowSwitch}
              setPrimaryRowSwitch={setPrimaryRowSwitch}
              setPrimaryRowSwitchState={setPrimaryRowSwitchState}
              assignedId={content.assignedId}
            />
          </RowValueContainer>
        );
      }
      return (
        <Shimmer>
          <RowValueContainer shimmer>
            <ShimmerRowValue />
          </RowValueContainer>
        </Shimmer>
      );
    }

    // Handle other columns
    return rowValueDecider(primaryRow.name, isAdding);
  };

  const renderPrimaryRowHeader = (primaryRow) => {
    if (!primaryRowSwitch[primaryRow.name]) {
      return null;
    }

    // Handle 'set' column
    if (primaryRow.name === 'set') {
      if (!isAdding) {
        return (
          <SetPrimaryValueContainer>
            {`${capitalizeWords(primaryRow.name)}s`}
          </SetPrimaryValueContainer>
        );
      }
      return (
        <Shimmer>
          <SetPrimaryValueContainer shimmer>
            {capitalizeWords(primaryRow.name)}
          </SetPrimaryValueContainer>
        </Shimmer>
      );
    }

    // Handle 'add-property' column
    if (primaryRow.name === 'add-property') {
      if (!isAdding) {
        return (
          <PrimaryValueContainer>
            <AddPropertyColumnComponent
              mode='header'
              primaryRows={primaryRows}
              primaryRowSwitch={primaryRowSwitch}
              setPrimaryRowSwitch={setPrimaryRowSwitch}
              setPrimaryRowSwitchState={setPrimaryRowSwitchState}
              assignedId={content.assignedId}
            />
          </PrimaryValueContainer>
        );
      }
      return (
        <Shimmer>
          <PrimaryValueContainer shimmer>
            <PrimaryRowValue isAdding={isAdding}>
              Add Property
            </PrimaryRowValue>
          </PrimaryValueContainer>
        </Shimmer>
      );
    }

    // Handle other columns
    if (!isAdding) {
      return (
        <PrimaryValueContainer>
          <PrimaryRowValue isAdding={isAdding}>
            {primaryRow.name === 'reps' || primaryRow.name === 'velocity' || primaryRow.name === 'peak power' ? (
              <NameAndDropdown>
                {(primaryRow.name === 'peak power' || primaryRow.name === 'reps' || primaryRow.name === 'velocity') && (
                  <InfoIconWrapper>
                    <IcomoonReact
                      iconSet={iconSet}
                      size={14}
                      icon='info2'
                      color='rgba(189, 189, 189, 1)'
                      style={{ cursor: 'pointer' }}
                      data-tooltip-id={getTooltipId(primaryRow.name)}
                    />
                  </InfoIconWrapper>
                )}

                <SwapPropDropdown
                  setPrimaryRowSwitch={setPrimaryRowSwitch}
                  primaryRowSwitch={primaryRowSwitch}
                  rowName={primaryRow.name}
                  size={12}
                  updateColumnName={updateColumnName}
                  name={primaryRow.name === 'load' ? capitalizeWords('Load') : primaryRow.label || capitalizeWords(primaryRow.name)}
                  unit={primaryRow.name === 'load' ? findCorrectUnit(content) : null}
                  peakPowerEnum={peakPowerEnum}
                  setPeakPowerEnum={setPeakPowerEnum}
                  setIsBodyweight={setIsBodyweight}
                  setIsTime={setIsTime}
                />
              </NameAndDropdown>
            ) : (
              <NameAndDropdown>
                <SwapPropDropdown
                  setPrimaryRowSwitch={setPrimaryRowSwitch}
                  primaryRowSwitch={primaryRowSwitch}
                  rowName={primaryRow.name}
                  size={12}
                  updateColumnName={updateColumnName}
                  name={primaryRow.name === 'load' ? capitalizeWords('Load') : primaryRow.label || capitalizeWords(primaryRow.name)}
                  unit={primaryRow.name === 'load' ? findCorrectUnit(content) : null}
                  peakPowerEnum={peakPowerEnum}
                  setPeakPowerEnum={setPeakPowerEnum}
                  setIsBodyweight={setIsBodyweight}
                  setIsTime={setIsTime}
                />
              </NameAndDropdown>
            )}
          </PrimaryRowValue>
        </PrimaryValueContainer>
      );
    }

    return (
      <Shimmer>
        <PrimaryValueContainer shimmer>
          <PrimaryRowValue isAdding={isAdding}>
            <NameAndDropdown>
              {primaryRow.name === 'load' ? capitalizeWords('Load') : capitalizeWords(primaryRow.name)}
              {' '}
              {primaryRow.name === 'load' ? findCorrectUnit(content) : null}
            </NameAndDropdown>
          </PrimaryRowValue>
        </PrimaryValueContainer>
      </Shimmer>
    );
  };

  return (
    <FormWrapper ref={tableContainerRef} onSubmit={(e) => {
      e.preventDefault();
      handleSaveCalendarExercise(contentIndex);
    }}
    >
      <Container>
        <PrimaryRow isAdding={isAdding}>
          {primaryRows.map((primaryRow) => renderPrimaryRowHeader(primaryRow))}
        </PrimaryRow>
        <ValueRow>
          <ErrorAndRowsContainer>
            <RowsContainer>
              {primaryRows.map((primaryRow) => renderPrimaryRowValue(primaryRow))}
            </RowsContainer>
            {error
            && (
            <ErrorText
              containerWidthForSetRowValueContainer={containerWidthForSetRowValueContainer}
            >
              {error}
            </ErrorText>
            )}
          </ErrorAndRowsContainer>
        </ValueRow>
      </Container>
      <SaveButtonWrapper>
        {!isAdding ? (
          <SaveButtonContainer type='submit'>
            <SaveButtonContainerText>
              ADD
            </SaveButtonContainerText>
            <RemoveIcon>
              <IcomoonReact
                iconSet={iconSet}
                size={12}
                icon='down-arrow'
                color='white'
              />
            </RemoveIcon>
          </SaveButtonContainer>
        ) : (
          <Shimmer>
            <SaveButtonContainer shimmer>
              <SaveButtonContainerText>
                ADD
              </SaveButtonContainerText>
              <RemoveIcon>
                <IcomoonReact
                  iconSet={iconSet}
                  size={12}
                  icon='down-arrow'
                  color='transparent'
                />
              </RemoveIcon>
            </SaveButtonContainer>
          </Shimmer>
        )}
      </SaveButtonWrapper>

      {/* ReactTooltip components */}
      <ReactTooltip
        id={repsTooltipId}
        place='top'
        style={{
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: 'white',
          fontFamily: 'Nunito Sans',
          fontSize: '13px',
          maxWidth: '250px',
          padding: '10px 16px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Text fontSize='13px' fontWeight={400} color='white'>Accepted Inputs</Text>
          <ul style={{ paddingLeft: '20px', margin: 0 }}>
            <li>
              <Text fontSize='13px' fontWeight={400} color='white'>Absolute (e.g. 5)</Text>
            </li>
            <li><Text fontSize='13px' fontWeight={400} color='white'>Comma Separated (e.g. 12,10,8)</Text></li>
            <li><Text fontSize='13px' fontWeight={400} color='white'>Range (e.g. 8-12)</Text></li>
            <li><Text fontSize='13px' fontWeight={400} color='white'>Minimum (e.g. 8+)</Text></li>
            <li><Text fontSize='13px' fontWeight={400} color='white'>&quot;AMAP&quot; for as many as possible</Text></li>
          </ul>
        </div>
      </ReactTooltip>

      <ReactTooltip
        id={peakPowerTooltipId}
        place='top'
        content='Athlete input only'
        style={{
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: 'white',
          fontFamily: 'Nunito Sans',
          fontSize: '13px',
        }}
      />

      <ReactTooltip
        id={velocityTooltipId}
        place='top'
        content='Athlete input only'
        style={{
          backgroundColor: 'rgba(68, 68, 68, 1)',
          color: 'white',
          fontFamily: 'Nunito Sans',
          fontSize: '13px',
        }}
      />
    </FormWrapper>
  );
};

PreSaveTable.propTypes = {
  coachCompletion: PropTypes.bool.isRequired,
  trackVolumeLoad: PropTypes.bool.isRequired,
  forcePrUpdate: PropTypes.bool.isRequired,
  disablePrUpdate: PropTypes.bool.isRequired,
  isLeftAndRight: PropTypes.bool.isRequired,
  completionOnly: PropTypes.bool.isRequired,
  trackRepCount: PropTypes.bool.isRequired,
  contentIndex: PropTypes.number.isRequired,
  content: PropTypes.instanceOf(Object).isRequired,
  swapDropdownSelectedExercise: PropTypes.instanceOf(Object).isRequired,
  localAdditionalInfo: PropTypes.string.isRequired,
  measure: PropTypes.func.isRequired,
  date: PropTypes.string,
  dayNumber: PropTypes.string,
  setCoachCompletion: PropTypes.func.isRequired,
  setCompletionOnly: PropTypes.func.isRequired,
  setIsLeftAndRight: PropTypes.func.isRequired,
  setTrackVolumeLoad: PropTypes.func.isRequired,
  setTrackRepCount: PropTypes.func.isRequired,
  setForcePrUpdate: PropTypes.func.isRequired,
  setDisablePrUpdate: PropTypes.func.isRequired,
};

PreSaveTable.defaultProps = {
  date: null,
  dayNumber: null,
};

export default PreSaveTable;
